/**
 * Platform Admin Session P0 spec item 5 — "Security/System Settings"
 * Session panel: real, non-secret facts about the CURRENT admin
 * session (current login/session creation time, last activity via the
 * rolling renewal, remaining access-token time, idle timeout, current
 * admin account). Deliberately never renders a raw token, refresh
 * token, or signing secret — the backend (`GET /api/saas/platform/session`)
 * never even sends them.
 */
import { ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons'
import { useCallback, useEffect, useState } from 'react'
import { platformGetSessionInfo } from '../api'
import type { PlatformSessionInfoOut } from '../types'
import { SectionCard, formatDateTime, formatRelativeTime } from './adminShared'
import { platformColors } from './theme'

function formatDuration(seconds: number): string {
  if (seconds <= 0) return '0 秒'
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const parts: string[] = []
  if (days > 0) parts.push(`${days} 天`)
  if (hours > 0) parts.push(`${hours} 小时`)
  if (days === 0 && minutes > 0) parts.push(`${minutes} 分钟`)
  if (parts.length === 0) parts.push(`${seconds} 秒`)
  return parts.join(' ')
}

function InfoRow({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        padding: '10px 4px',
        borderBottom: `1px solid ${platformColors.border}`,
        gap: 16,
      }}
    >
      <div style={{ fontSize: 13, color: platformColors.secondaryText, flexShrink: 0 }}>{label}</div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 13, color: platformColors.primaryText, fontWeight: 500 }}>{value}</div>
        {hint && <div style={{ fontSize: 11, color: platformColors.mutedText, marginTop: 2 }}>{hint}</div>}
      </div>
    </div>
  )
}

export default function PlatformSecurityPage() {
  const [info, setInfo] = useState<PlatformSessionInfoOut | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(() => {
    setLoading(true)
    platformGetSessionInfo()
      .then((res) => {
        setInfo(res.data)
        setError(null)
      })
      .catch((err) => setError(err.message || '加载失败'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
    // Light poll (not the Overview page's 10s aggressive rate — this
    // panel is informational, not an operational dashboard) so
    // "剩余时间"/"最近活动" stay reasonably fresh if left open.
    const id = setInterval(load, 30_000)
    return () => clearInterval(id)
  }, [load])

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ fontSize: 20, lineHeight: '26px', fontWeight: 600, color: platformColors.primaryText, marginBottom: 4 }}>
        安全管理
      </div>
      <div style={{ fontSize: 12, color: platformColors.secondaryText, marginBottom: 16 }}>
        当前 Platform Admin 会话信息 — 从不展示原始 Token / Refresh Token / 签名密钥
      </div>

      {error && (
        <div
          style={{
            background: platformColors.dangerBg,
            color: platformColors.danger,
            border: `1px solid ${platformColors.dangerBg}`,
            borderRadius: 8,
            padding: '7px 14px',
            marginBottom: 12,
            fontSize: 12,
          }}
        >
          加载会话信息失败：{error}
        </div>
      )}

      <SectionCard
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <SafetyCertificateOutlined /> 当前会话
          </span>
        }
        extra={
          <button
            type="button"
            onClick={load}
            disabled={loading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 26,
              padding: '0 10px',
              borderRadius: 6,
              border: `1px solid ${platformColors.border}`,
              background: '#fff',
              color: platformColors.primaryText,
              fontSize: 12,
              cursor: loading ? 'default' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            <ReloadOutlined spin={loading} /> 刷新
          </button>
        }
      >
        {info ? (
          <div style={{ padding: '2px 10px' }}>
            <InfoRow label="当前管理员账号" value={info.admin_email} />
            <InfoRow
              label="Session 创建时间（登录时间）"
              value={formatDateTime(info.session_started_at)}
              hint={formatRelativeTime(info.session_started_at)}
            />
            <InfoRow
              label="认证方式"
              value={info.session_source === 'platform_session' ? 'Platform 专用会话（滚动续期）' : '共享会话（兼容回退路径）'}
              hint={
                info.session_source === 'platform_session'
                  ? '与普通租户用户登录使用独立 Cookie，互不影响'
                  : '下一次请求将自动升级为 Platform 专用会话'
              }
            />
            <InfoRow
              label="Access Token 剩余有效期"
              value={formatDuration(info.access_token_remaining_seconds)}
              hint={`到期时间：${formatDateTime(info.access_token_expires_at)}`}
            />
            <InfoRow
              label="Idle Timeout（空闲超时）"
              value={formatDuration(info.idle_timeout_seconds)}
              hint={info.rolling ? '只要持续操作（含后台自动刷新），会话会自动续期；超过此时长无任何活动才需要重新登录' : '该会话不会自动续期'}
            />
            <InfoRow label="Session 最大有效期" value={formatDuration(info.session_max_age_seconds)} />
          </div>
        ) : (
          !error && <div style={{ padding: 16, fontSize: 12, color: platformColors.mutedText }}>加载中…</div>
        )}
      </SectionCard>
    </div>
  )
}
